import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import { FiSearch, FiBell, FiAlertTriangle, FiCheckCircle, FiClock, FiUser, FiLoader } from 'react-icons/fi';
import { getStatusColor } from '../../data/helpers';
import { dataFetch } from '../../api';
import { sendReminderEmail } from '../../services/resendemailService';

const getLearnerStatus = (pct, learner) => {
  if (pct === 100) return 'Completed';
  if (learner.trainingStatus === 'Overdue') return 'Overdue';
  if (pct > 0) return 'In Progress';
  return 'Not Started';
};

const CampaignProgressTable = ({ campaign, assignedLearners, assignments = [] }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingReminder, setSendingReminder] = useState(null);

  const currentYear = new Date().getFullYear();
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    const fetchReminders = async () => {
      setLoading(true);
      try {
        const res = await dataFetch(`read/campaign_reminder_sent?campaign_id=${campaign.id}`);
        if (res?.status === 'success') {
          setReminders(res.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch reminders', err);
      } finally {
        setLoading(false);
      }
    };
    if (campaign?.id) {
      fetchReminders();
    }
  }, [campaign?.id]);

  const handleSendReminder = async (learner) => {
    const existingToday = reminders.find(
      r => String(r.employee_id) === String(learner.id) && r.date === today
    );

    if (existingToday) return;

    setSendingReminder(learner.id);
    try {
      const emailResult = await sendReminderEmail({
        toEmail: learner.email,
        toName: `${learner.firstName} ${learner.lastName}`,
        subject: 'Training Reminder',
        message: `This is a reminder to please complete your training for the campaign: ${campaign.campaignName}. Due Date: ${campaign.endDate}`,
      });

      if (!emailResult?.success) {
        throw new Error(emailResult?.error || 'Failed to send reminder.');
      }

      const payload = {
        campaign_id: String(campaign.id),
        employee_id: String(learner.id),
        year: currentYear,
        date: today,
        sent_frequency: 1,
        user_id: localStorage.getItem('user_id') || 'system'
      };
      
      const res = await dataFetch(`create/campaign_reminder_sent`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (res?.status === 'success') {
        const refreshRes = await dataFetch(`read/campaign_reminder_sent?campaign_id=${campaign.id}`);
        if (refreshRes?.status === 'success') {
          setReminders(refreshRes.data || []);
        } else {
          setReminders(prev => [...prev, res.data]);
        }
      }
    } catch (err) {
      console.error('Reminder error:', err);
      alert('Failed to send reminder.');
    } finally {
      setSendingReminder(null);
    }
  };

  const rows = assignedLearners.map(l => {
    const myReminders = reminders.filter(r => String(r.employee_id) === String(l.id));
    const totalReminders = myReminders.reduce((sum, r) => sum + (r.sent_frequency || 0), 0);
    const sentToday = myReminders.some(r => r.date === today);

    const learnerAssignments = assignments.filter(a => String(a.employee_id) === String(l.id));
    let pct = 0;
    if (learnerAssignments.length > 0) {
      const completed = learnerAssignments.filter(a => a.status === 'Completed').length;
      pct = Math.round((completed / learnerAssignments.length) * 100);
    }

    return {
      learner: l,
      pct,
      sentFrequency: totalReminders,
      sentToday,
    };
  }).map(r => ({ ...r, status: getLearnerStatus(r.pct, r.learner) }));

  const statuses = ['All', 'Completed', 'In Progress', 'Not Started', 'Overdue'];
  const filtered = rows.filter(r => {
    const name = `${r.learner.firstName} ${r.learner.lastName}`.toLowerCase();
    return (statusFilter === 'All' || r.status === statusFilter) &&
      (name.includes(search.toLowerCase()) || r.learner.email.toLowerCase().includes(search.toLowerCase()));
  });

  const nonCompliant = rows.filter(r => r.status === 'Overdue' || r.status === 'Not Started');

  return (
    <div className="space-y-4">
      {nonCompliant.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <SafeIcon icon={FiAlertTriangle} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-700">{nonCompliant.length} non-compliant learner{nonCompliant.length > 1 ? 's' : ''}</p>
            <p className="text-xs text-red-600 mt-0.5">{nonCompliant.map(r => `${r.learner.firstName} ${r.learner.lastName}`).join(', ')}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search learners…"
              className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500" />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {statuses.map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${statusFilter === s ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center p-8 text-slate-400">
              <SafeIcon icon={FiLoader} className="animate-spin text-2xl" />
            </div>
          ) : (
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                  <th className="p-4">Learner</th>
                  <th className="p-4">Progress</th>
                  <th className="p-4">Status</th>
                  <th className="p-4">Sent Frequency</th>
                  <th className="p-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="text-sm divide-y divide-slate-100">
                {filtered.map(({ learner: l, pct, sentFrequency, sentToday, status }) => (
                  <tr key={l.id} className="hover:bg-slate-50 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate(`/learners/${l.id}`)}>
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-violet-500 text-white flex items-center justify-center font-bold text-xs flex-shrink-0">
                          {l.firstName?.[0] || ''}{l.lastName?.[0] || ''}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800 hover:text-blue-600 transition-colors">{l.firstName} {l.lastName}</p>
                          <p className="text-xs text-slate-400">{l.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="w-20 bg-slate-100 rounded-full h-1.5 flex-shrink-0">
                          <div className={`h-1.5 rounded-full ${pct === 100 ? 'bg-emerald-500' : pct >= 50 ? 'bg-blue-500' : 'bg-red-400'}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs font-semibold text-slate-600">{pct}%</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${getStatusColor(status)}`}>{status}</span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1.5 text-slate-500">
                        <SafeIcon icon={FiBell} className="text-xs text-slate-400" />
                        <span className="text-xs">{sentFrequency}</span>
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <button
                        onClick={() => handleSendReminder(l)}
                        disabled={sendingReminder === l.id || sentToday}
                        className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${
                          sendingReminder === l.id ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed' :
                          sentToday ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed' :
                          sentFrequency > 0 ? 'bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100' : 'bg-white text-slate-600 border-slate-200 hover:bg-orange-50 hover:text-orange-600 hover:border-orange-200'
                        }`}>
                        {sendingReminder === l.id ? 'Sending...' : (sentToday ? 'Sent Today' : (sentFrequency > 0 ? '✓ Send Again' : 'Send Reminder'))}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {!loading && (
          <div className="px-4 py-3 border-t border-slate-100 text-xs text-slate-500 bg-slate-50">
            Showing {filtered.length} of {assignedLearners.length} learners
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignProgressTable;